import { useState, type CSSProperties } from 'react';
import type { ProductServiceResource } from '@neuroedge/shared';
import { Button } from '../../components/ui';
import { useProductsServices } from '../../api/products-services';
import {
  applyOverallDiscount,
  blankRow,
  editorTotals,
  rowAmounts,
  type EditorRow,
} from './quotation-editor-model';
import { formatMoney } from './quotation-format';

const cellInput: CSSProperties = { width: '100%', minWidth: 64 };
const errorText: CSSProperties = {
  display: 'block',
  color: 'var(--color-danger)',
  fontSize: 'var(--font-size-sm)',
};

function FieldError({ message }: { message?: string }) {
  return message ? <span style={errorText}>{message}</span> : null;
}

/** The price a product is quoted at by default - its discounted catalog price when set. */
const catalogPrice = (p: ProductServiceResource) => p.discountedPriceAmount ?? p.priceAmount;

/**
 * Editable quotation lines - shared by "Edit quotation" and the New
 * Quotation review step. Item (catalog product, or a named price-on-request
 * line) / Qty / Price / Discount % / Discounted price / Total, live totals,
 * and an "Overall discount" that shows the effective average across lines
 * and, when typed into, sets that discount on every priced line.
 */
export function QuotationItemsEditor({
  rows,
  onChange,
  currency,
  errors,
}: {
  rows: EditorRow[];
  onChange: (rows: EditorRow[]) => void;
  currency: string;
  errors: Record<string, string>;
}) {
  const catalog = useProductsServices({ status: 'ACTIVE', pageSize: 100 });
  const products = (catalog.data?.items ?? []).filter((p) => catalogPrice(p) !== null);
  const totals = editorTotals(rows);
  const [overallDraft, setOverallDraft] = useState<string | null>(null);

  const update = (i: number, patch: Partial<EditorRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  function pickProduct(i: number, productServiceId: string) {
    const row = rows[i]!;
    if (!productServiceId) {
      update(i, { productServiceId: '', productName: '', unitPrice: '', needsPick: false });
      return;
    }
    const candidate = row.candidates.find((c) => c.productServiceId === productServiceId);
    const product = products.find((p) => p.id === productServiceId);
    const name = candidate?.name ?? product?.name ?? row.productName;
    const price = candidate?.unitPrice ?? (product ? catalogPrice(product) : null);
    update(i, {
      productServiceId,
      productName: name,
      name,
      unitPrice: price !== null && price !== undefined ? String(price) : row.unitPrice,
      unit: row.unit ?? product?.unit ?? null,
      needsPick: false,
    });
  }

  const pricedCount = rows.filter((r) => r.productServiceId).length;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table className="ne-table" style={{ minWidth: 760 }}>
          <thead>
            <tr>
              <th>Item</th>
              <th style={{ width: 90 }}>Qty</th>
              <th style={{ width: 110 }}>Price</th>
              <th style={{ width: 90 }}>Discount %</th>
              <th style={{ width: 110 }}>Discounted price</th>
              <th style={{ width: 110 }}>Total</th>
              <th style={{ width: 40 }} aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const amounts = rowAmounts(row);
              const priced = row.productServiceId !== '';
              const knownIds = new Set([
                ...row.candidates.map((c) => c.productServiceId),
                ...products.map((p) => p.id),
              ]);
              return (
                <tr key={row.key} style={{ cursor: 'default', verticalAlign: 'top' }}>
                  <td>
                    <select
                      className="ne-input"
                      aria-label={`Item ${i + 1} product`}
                      value={row.productServiceId}
                      onChange={(e) => pickProduct(i, e.target.value)}
                      style={cellInput}
                    >
                      <option value="">Price on request (no catalog product)</option>
                      {row.candidates.length > 0 ? (
                        <optgroup label="Suggested">
                          {row.candidates.map((c) => (
                            <option key={c.productServiceId} value={c.productServiceId}>
                              {c.name} — {c.currency} {c.unitPrice}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                      <optgroup label="Catalog">
                        {products
                          .filter((p) => !row.candidates.some((c) => c.productServiceId === p.id))
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} — {p.currency} {catalogPrice(p)}
                            </option>
                          ))}
                      </optgroup>
                      {priced && !knownIds.has(row.productServiceId) ? (
                        <option value={row.productServiceId}>{row.productName}</option>
                      ) : null}
                    </select>
                    {!priced ? (
                      <input
                        className="ne-input"
                        aria-label={`Item ${i + 1} name`}
                        placeholder="Item name"
                        value={row.name}
                        onChange={(e) => update(i, { name: e.target.value })}
                        style={{ ...cellInput, marginTop: 'var(--space-1)' }}
                      />
                    ) : null}
                    {row.rawText && !row.needsPick && row.rawText !== row.name ? (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-muted)',
                        }}
                        title="What the customer wrote"
                      >
                        {row.rawText}
                      </span>
                    ) : null}
                    {row.needsPick ? (
                      <span style={{ ...errorText, color: 'var(--color-text-muted)' }}>
                        Pick the right match{row.rawText ? ` for “${row.rawText}”` : ''}.
                      </span>
                    ) : null}
                    <FieldError message={errors[`${i}.name`]} />
                  </td>
                  <td>
                    <input
                      className="ne-input"
                      inputMode="decimal"
                      aria-label={`Item ${i + 1} quantity`}
                      value={row.quantity}
                      onChange={(e) => update(i, { quantity: e.target.value })}
                      style={cellInput}
                    />
                    {row.unit ? (
                      <span style={{ fontSize: 'var(--font-size-sm)' }}>{row.unit}</span>
                    ) : null}
                    <FieldError message={errors[`${i}.quantity`]} />
                  </td>
                  {priced ? (
                    <>
                      <td>
                        <input
                          className="ne-input"
                          inputMode="decimal"
                          aria-label={`Item ${i + 1} price`}
                          value={row.unitPrice}
                          onChange={(e) => update(i, { unitPrice: e.target.value })}
                          style={cellInput}
                        />
                        <FieldError message={errors[`${i}.unitPrice`]} />
                      </td>
                      <td>
                        <input
                          className="ne-input"
                          inputMode="decimal"
                          aria-label={`Item ${i + 1} discount percent`}
                          value={row.discountPercent}
                          onChange={(e) => update(i, { discountPercent: e.target.value })}
                          style={cellInput}
                        />
                        <FieldError message={errors[`${i}.discountPercent`]} />
                      </td>
                      <td>{amounts ? formatMoney(amounts.discountedUnitPrice, currency) : '—'}</td>
                      <td>{amounts ? formatMoney(amounts.lineTotal, currency) : '—'}</td>
                    </>
                  ) : (
                    <td colSpan={4} style={{ color: 'var(--color-text-muted)' }}>
                      Price on request
                    </td>
                  )}
                  <td>
                    <button
                      type="button"
                      className="ne-modal__close"
                      aria-label={`Remove item ${i + 1}`}
                      onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <FieldError message={errors.items} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="secondary" onClick={() => onChange([...rows, blankRow()])}>
          Add item
        </Button>
        <div style={{ display: 'grid', gap: 'var(--space-1)', minWidth: 260 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-2)',
            }}
          >
            <span>Overall discount %</span>
            <input
              className="ne-input"
              inputMode="decimal"
              aria-label="Overall discount percent"
              disabled={pricedCount === 0}
              style={{ width: 90 }}
              value={overallDraft ?? String(totals.averageDiscountPercent)}
              onFocus={() => setOverallDraft(String(totals.averageDiscountPercent))}
              onBlur={() => setOverallDraft(null)}
              onChange={(e) => {
                const value = e.target.value;
                setOverallDraft(value);
                const n = Number(value);
                if (value.trim() !== '' && !Number.isNaN(n) && n >= 0 && n <= 100) {
                  onChange(applyOverallDiscount(rows, value));
                }
              }}
            />
          </label>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
            Shows the average across lines; typing here applies it to every item.
          </span>
          <QuotationSummary
            grossAmount={totals.grossAmount}
            discountAmount={totals.discountAmount}
            totalAmount={totals.totalAmount}
            currency={currency}
          />
        </div>
      </div>
    </div>
  );
}

/** Total price / Discount / Quotation price after discount. */
export function QuotationSummary({
  grossAmount,
  discountAmount,
  totalAmount,
  currency,
}: {
  grossAmount: number;
  discountAmount: number;
  totalAmount: number;
  currency: string;
}) {
  const row: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 'var(--space-3)',
  };
  return (
    <dl style={{ margin: 0, display: 'grid', gap: 2 }}>
      <div style={row}>
        <dt>Total price</dt>
        <dd style={{ margin: 0 }}>{formatMoney(grossAmount, currency)}</dd>
      </div>
      <div style={row}>
        <dt>Discount</dt>
        <dd style={{ margin: 0 }}>
          {discountAmount > 0 ? '− ' : ''}
          {formatMoney(discountAmount, currency)}
        </dd>
      </div>
      <div style={{ ...row, fontWeight: 600 }}>
        <dt>Quotation price after discount</dt>
        <dd style={{ margin: 0 }}>{formatMoney(totalAmount, currency)}</dd>
      </div>
    </dl>
  );
}
